import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { useRef } from 'react';

/**
 * Bootstrap 5 / Hyper-theme primitives — these mirror the markup emitted by
 * the legacy PHP/Laravel app so a re-skin is just a matter of swapping
 * components without touching call sites.
 *
 * Export shape is preserved from the previous Tailwind-based ui.tsx so
 * existing pages compile unchanged.
 */

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-danger'
  | 'dark'
  | 'link';

export function Button({
  variant = 'primary',
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  // Legacy: `btn btn-primary`. Earlier ui-kit treated `danger` as outline,
  // so keep that behaviour by mapping it to `btn-outline-danger`.
  const variantClass = variant === 'danger' ? 'btn-outline-danger' : `btn-${variant}`;
  return (
    <button
      type="button"
      {...rest}
      className={`btn ${variantClass} ${className ?? ''}`.trim()}
    />
  );
}

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`mb-3 ${className ?? ''}`.trim()}>
      <label className="form-label">{label}</label>
      {children}
      {hint && <small className="form-text text-muted d-block">{hint}</small>}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`form-control ${props.className ?? ''}`.trim()}
    />
  );
}

export function NumberInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="number"
      step="any"
      {...props}
      className={`form-control ${props.className ?? ''}`.trim()}
    />
  );
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`form-control ${props.className ?? ''}`.trim()}
    />
  );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`form-select ${props.className ?? ''}`.trim()}
    />
  );
}

export function Card({
  title,
  children,
  actions,
}: {
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="card">
      {(title || actions) && (
        <div className="card-header d-flex align-items-center justify-content-between">
          {title && <h4 className="header-title mb-0">{title}</h4>}
          {actions}
        </div>
      )}
      <div className="card-body">{children}</div>
    </div>
  );
}

export function ErrorAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" className="alert alert-danger">
      {message}
    </div>
  );
}

export function Pill({
  tone = 'slate',
  children,
}: {
  tone?: 'amber' | 'emerald' | 'sky' | 'slate';
  children: ReactNode;
}) {
  // Map legacy tones onto Bootstrap contextual badge classes.
  const tones: Record<string, string> = {
    amber: 'bg-warning text-dark',
    emerald: 'bg-success',
    sky: 'bg-info text-dark',
    slate: 'bg-secondary',
  };
  return <span className={`badge ${tones[tone]}`}>{children}</span>;
}

/**
 * Fade transition wrapper — uses the `.fade` CSS classes already defined
 * in the legacy `app.min.css` to animate wizard step transitions exactly
 * like the legacy React-in-Blade wizard did.
 */
export function FadeStep({
  in: inProp,
  children,
  unmountOnExit = true,
  appear = true,
}: {
  in: boolean;
  children: ReactNode;
  unmountOnExit?: boolean;
  appear?: boolean;
}) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  return (
    <CSSTransition
      in={inProp}
      timeout={300}
      classNames="fade"
      unmountOnExit={unmountOnExit}
      appear={appear}
      nodeRef={nodeRef}
    >
      <div ref={nodeRef}>{children}</div>
    </CSSTransition>
  );
}
